import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlayerCard } from "./PlayerCard";
import { FormationGrid } from "./FormationGrid";
import { Edit, Lock, User } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface League {
  id: string;
  name: string;
}

interface Team {
  id: string;
  user_id: string;
  league_id: string;
  owner_name?: string;
}

interface GameweekInfo {
  id: string;
  gw_number: number;
  lock_at: string;
  status: string;
}

interface EditabilityInfo {
  editable: boolean;
  reason?: string;
  lockAt: string;
  status: string;
}

interface Player {
  id: string;
  name: string;
  team: string;
  position: string;
  photo_url?: string;
  is_active: boolean;
}

interface RosterSlot {
  id: string;
  player_id: string;
  position: string;
  slot_type: string;
  player?: Player;
  is_captain?: boolean;
}

interface RosterViewProps {
  team: Team;
  league: League;
  gameweek: GameweekInfo | null;
  editability: EditabilityInfo | null;
  isOwnTeam: boolean;
}

export function RosterView({
  team,
  league,
  gameweek,
  editability,
  isOwnTeam,
}: RosterViewProps) {
  const [rosterSlots, setRosterSlots] = useState<RosterSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [captain, setCaptain] = useState<string | null>(null);

  const fetchRoster = async () => {
    // Always run fetch, even without gameweek to show empty state
    try {
      setLoading(true);
      
      if (!gameweek) {
        // No gameweek available, show empty roster
        setRosterSlots([]);
        return;
      }

      const { data, error } = await supabase
        .from('roster_slots')
        .select(`
          id,
          player_id,
          position,
          slot_type,
          players (
            id,
            name,
            team,
            position,
            photo_url,
            is_active
          )
        `)
        .eq('league_player_id', team.id)
        .eq('gameweek_id', gameweek.id);

      if (error) {
        console.error('Error fetching roster:', error);
        // Set empty roster on error
        setRosterSlots([]);
        return;
      }

      const slots = data?.map(slot => ({
        id: slot.id,
        player_id: slot.player_id,
        position: slot.position,
        slot_type: slot.slot_type,
        player: slot.players as Player | undefined,
      })) || [];

      setRosterSlots(slots);
    } catch (error) {
      console.error('Error fetching roster:', error);
      setRosterSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCaptainToggle = (playerId: string) => {
    if (!isOwnTeam || !editability?.editable) return;
    
    setCaptain(current => current === playerId ? null : playerId);
  };

  useEffect(() => {
    fetchRoster();
  }, [team, gameweek]);

  const starters = rosterSlots.filter(slot => slot.slot_type === 'starter');
  const bench = rosterSlots.filter(slot => slot.slot_type === 'bench');

  const getBannerInfo = () => {
    if (!isOwnTeam && team.owner_name) {
      return {
        variant: "default" as const,
        message: `Read-only: viewing ${team.owner_name}'s team`,
        icon: <User className="h-4 w-4" />
      };
    }
    
    if (editability && !editability.editable) {
      return {
        variant: "destructive" as const,
        message: `Read-only: ${editability.reason || `roster locked for Gameweek ${gameweek?.gw_number}`}`,
        icon: <Lock className="h-4 w-4" />
      };
    }
    
    return null;
  };

  const bannerInfo = getBannerInfo();
  const canEdit = isOwnTeam && editability?.editable;
  const hasPlayers = starters.length > 0 || bench.length > 0;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <Skeleton className="h-7 w-32" />
            <Skeleton className="h-10 w-24" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-80 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Roster
            {gameweek && (
              <Badge variant="outline">
                Gameweek {gameweek.gw_number}
              </Badge>
            )}
          </CardTitle>
          
          {canEdit && hasPlayers && (
            <Button variant="default" className="bg-gradient-primary">
              <Edit className="h-4 w-4 mr-2" />
              Edit Lineup
            </Button>
          )}
        </div>
        
        {bannerInfo && (
          <Alert variant={bannerInfo.variant}>
            <div className="flex items-center gap-2">
              {bannerInfo.icon}
              <AlertDescription>{bannerInfo.message}</AlertDescription>
            </div>
          </Alert>
        )}
      </CardHeader>
      
      <CardContent>
        {!hasPlayers ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <User className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-lg font-medium mb-2">No players yet</p>
            <p className="text-muted-foreground mb-4">
              Complete the draft to build your team
            </p>
            {canEdit && (
              <Button variant="outline">
                Join Draft Room
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Formation Grid - 11 starters */}
            <div>
              <h3 className="font-medium mb-3">Formation (4-3-3)</h3>
              <FormationGrid
                starters={starters}
                captain={captain}
                onCaptainToggle={handleCaptainToggle}
                canEdit={canEdit}
              />
            </div>

            {/* Bench - 10 slots */}
            <div>
              <h3 className="font-medium mb-3">Bench</h3>
              <div className="grid grid-cols-5 gap-3">
                {Array.from({ length: 10 }).map((_, index) => {
                  const benchPlayer = bench[index];
                  
                  return (
                    <div key={index}>
                      {benchPlayer?.player ? (
                        <PlayerCard
                          player={benchPlayer.player}
                          isCaptain={false}
                          onCaptainToggle={() => {}}
                          showCaptainControl={false}
                          size="small"
                        />
                      ) : (
                        <div className="aspect-[3/4] rounded-lg border-2 border-dashed border-muted flex items-center justify-center text-xs text-muted-foreground text-center p-2">
                          Bench {index + 1}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}